<template>
  <div class="hws-page">
    <div class="hws-bg">
      <div class="hws-bg-gradient" />
    </div>

    <div class="hws-main" :style="{ paddingTop: statusBarHeight + 'px' }">
      <header class="hws-nav">
        <button type="button" class="hws-nav-back" aria-label="返回" @click="goBack">
          <i class="el-icon-arrow-left" />
        </button>
        <h1 class="hws-nav-title">健康周报</h1>
        <button type="button" class="hws-history-btn" @click="goHistoryList">
          历史报告
        </button>
      </header>

      <div v-if="loading" class="hws-loading">报告加载中...</div>
      <div v-else-if="loadError" class="hws-loading">
        <p>{{ loadError }}</p>
        <button type="button" class="hws-retry" @click="fetchReport">重试</button>
      </div>

      <template v-else>
      <div v-if="dateRange" class="hws-content-date">{{ dateRange }}</div>

      <div
        class="hws-module-tabs-wrap"
        :class="{
          'is-scroll-left': tabsCanScrollLeft,
          'is-scroll-right': tabsCanScrollRight
        }"
      >
        <nav
          ref="moduleTabs"
          class="hws-module-tabs"
          aria-label="周报模块"
          @scroll.passive="updateTabsScrollHint"
        >
          <button
            v-for="tab in visibleModuleTabs"
            :key="tab.key"
            type="button"
            class="hws-module-tab"
            :class="{ active: activeModule === tab.key }"
            @click="switchModule(tab.key)"
          >
            {{ tab.label }}
          </button>
        </nav>
        <span
          v-show="tabsCanScrollLeft"
          class="hws-tabs-hint hws-tabs-hint--left"
          aria-hidden="true"
        >
          <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
            <path d="M7.5 2.5L4 6l3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span
          v-show="tabsCanScrollRight"
          class="hws-tabs-hint hws-tabs-hint--right"
          aria-hidden="true"
        >
          <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
            <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </div>

      <div class="hws-module-body">
        <!-- 健康趋势 -->
        <template v-if="activeModule === 'trend'">
          <section class="hws-card">
            <div class="hws-card-head">
              <h2 class="hws-card-title">一周健康状态趋势</h2>
            </div>
            <div ref="trendChart" class="hws-chart hws-chart--trend" />
          </section>

          <section v-if="insights.length" class="hws-card">
            <div class="hws-card-head">
              <h2 class="hws-card-title">本周要点</h2>
            </div>
            <ul class="hws-insights">
              <li v-for="(row, index) in insights" :key="'ins-' + index" class="hws-insight">
                <span class="hws-insight-dot" />
                <p class="hws-insight-text" v-html="row" />
              </li>
            </ul>
          </section>
        </template>

        <!-- 健康预警 -->
        <section v-else-if="activeModule === 'warn'" class="hws-card hws-card--warn">
          <div class="hws-card-head hws-card-head--warn">
            <h2 class="hws-card-title">健康预警</h2>
          </div>
          <p v-if="warnEmptyCopy && !warnTypes.length" class="hws-empty">{{ warnEmptyCopy }}</p>
          <template v-else>
            <div ref="warnChart" class="hws-chart hws-chart--warn" />
            <ul class="hws-warn-list">
              <li
                v-for="item in warnTypes"
                :key="item.warnType || item.name"
                class="hws-warn-item"
              >
                <span class="hws-warn-dot" :style="{ background: item.color }" />
                <span class="hws-warn-name">{{ item.name }}</span>
                <span class="hws-warn-count">{{ item.count }}次</span>
              </li>
            </ul>
          </template>
        </section>

        <!-- 中医五藏 -->
        <section v-else-if="activeModule === 'tcm'" class="hws-card">
          <div class="hws-card-head">
            <h2 class="hws-card-title">中医五藏态势</h2>
          </div>
          <template v-if="tcmEvaluable">
            <div class="hws-organ-tabs">
              <button
                v-for="org in organTabs"
                :key="org.key"
                type="button"
                class="hws-organ-tab"
                :class="{ active: activeOrgan === org.key }"
                @click="activeOrgan = org.key"
              >
                {{ org.label }}
              </button>
            </div>

            <div class="hws-sub-title">{{ currentOrganAssessTitle }}</div>
            <div v-if="currentOrganStatus" class="hws-hero-value">{{ currentOrganStatus }}</div>
            <div v-if="hasCurrentOrganScores" ref="organScoreChart" class="hws-chart hws-chart--bar" />
            <p v-else class="hws-empty">暂无数据</p>

            <template v-if="organDimensions.length">
              <div class="hws-dim-tabs">
                <div
                  v-for="dim in organDimensions"
                  :key="dim.key"
                  class="hws-dim-tab"
                  :class="scoreToneClass(dim.score)"
                >
                  <span class="hws-dim-tab-label">{{ dim.label }}</span>
                  <span class="hws-dim-tab-score">
                    {{ dim.score }}<span class="hws-dim-tab-unit">分</span>
                  </span>
                </div>
              </div>
              <div class="hws-dim-info">
                <div class="hws-dim-row">
                  <span class="hws-dim-label">常见症状</span>
                  <span class="hws-dim-value">{{ currentHeartDim.symptoms }}</span>
                </div>
                <div class="hws-dim-row">
                  <span class="hws-dim-label">好发疾病</span>
                  <span class="hws-dim-value">{{ currentHeartDim.diseases }}</span>
                </div>
              </div>
            </template>
          </template>
          <p v-else class="hws-empty">佩戴时间不足，暂无法评估中医五藏态势</p>
        </section>

        <!-- 心率趋势 -->
        <template v-else-if="activeModule === 'hr'">
          <section class="hws-card">
            <div class="hws-hero-metric">
              <div class="hws-hero-value">{{ hrRangeText }}</div>
              <div class="hws-hero-unit">次/分 · {{ hrPeakDay }}</div>
            </div>
            <div ref="hrRangeChart" class="hws-chart hws-chart--range" />
          </section>

          <section class="hws-card">
            <div class="hws-card-head">
              <h2 class="hws-card-title">心率周统计</h2>
            </div>
            <ul class="hws-metric-list">
              <li v-for="item in hrWeekStats" :key="item.label" class="hws-metric-row">
                <span class="hws-metric-label">{{ item.label }}</span>
                <span class="hws-metric-value" :class="metricValueClass(item)">{{ item.value }}</span>
                <span class="hws-status" :class="metricStatusClass(item)">
                  {{ metricStatusIcon(item) }}
                </span>
              </li>
            </ul>
            <div v-if="hrIdealRanges.length" class="hws-range-pills">
              <div v-for="item in hrIdealRanges" :key="item.label" class="hws-range-pill">
                <span>{{ item.label }}</span>
                <strong v-if="item.value">{{ item.value }}</strong>
              </div>
            </div>
            <div v-if="hrAbnormalText || heartAiAdviceList.length" class="hws-ai-block">
              <div class="hws-sub-title">本周异常预警</div>
              <ul class="hws-advice">
                <li v-if="hrAbnormalText" v-html="hrAbnormalText" />
                <li v-for="(tip, i) in heartAiAdviceList" :key="'hr-ai-' + i">{{ tip }}</li>
              </ul>
            </div>
          </section>
        </template>

        <!-- 血压趋势 -->
        <template v-else-if="activeModule === 'bp'">
          <section class="hws-card">
            <div class="hws-card-head">
              <h2 class="hws-card-title">手表血压监测</h2>
            </div>
            <p v-if="!hasWatchBp" class="hws-empty">{{ watchBpEmptyCopy }}</p>
            <template v-else>
              <div ref="watchBpChart" class="hws-chart hws-chart--range" />
              <ul class="hws-metric-list">
                <li v-for="item in watchBpStats" :key="item.label" class="hws-metric-row">
                  <span class="hws-metric-label">{{ item.label }}</span>
                  <span class="hws-metric-value" :class="metricValueClass(item)">{{ item.value }}</span>
                  <span class="hws-status" :class="metricStatusClass(item)">
                    {{ metricStatusIcon(item) }}
                  </span>
                </li>
              </ul>
            </template>
            <p class="hws-card-note">偶发波动且无不适，周最高值仅参考，无需焦虑。</p>
          </section>

          <section class="hws-card">
            <div class="hws-card-head">
              <h2 class="hws-card-title">血压计监测</h2>
            </div>
            <p v-if="!hasMeterBp" class="hws-empty">{{ meterEmptyCopy }}</p>
            <template v-else>
              <p class="hws-hint">建议每天固定时间测量，统计趋势变化</p>
              <div ref="meterBpChart" class="hws-chart hws-chart--range" />
            </template>
          </section>

          <section v-if="bpRefRanges.length" class="hws-card hws-card--bp-advice">
            <div class="hws-card-head">
              <h2 class="hws-card-title">血压参考</h2>
            </div>
            <div class="hws-bp-ref-section">
              <div class="hws-bp-section-title">
                <div class="hws-bp-section-title-main">
                  <span class="hws-bp-section-bar" />
                  <span>参考范围</span>
                </div>
                <span class="hws-bp-section-tag">基于健康档案</span>
              </div>
              <div class="hws-bp-ref-grid">
                <div
                  v-for="item in bpRefRanges"
                  :key="item.label"
                  class="hws-bp-ref-item"
                  :class="{ 'is-diastolic': item.label.indexOf('舒张') >= 0 }"
                >
                  <div class="hws-bp-ref-icon">{{ item.label.indexOf('收缩') >= 0 ? '高' : '低' }}</div>
                  <div class="hws-bp-ref-content">
                    <div class="hws-bp-ref-label">{{ item.label }}</div>
                    <div class="hws-bp-ref-value">{{ item.value }}</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section v-if="bpAdviceList.length" class="hws-card hws-card--bp-advice">
            <div class="hws-card-head">
              <h2 class="hws-card-title">AI 健康建议</h2>
            </div>
            <ul class="hws-advice">
              <li v-for="(tip, i) in bpAdviceList" :key="'bp-adv-' + i">{{ tip }}</li>
            </ul>
          </section>
        </template>

        <!-- 睡眠趋势 -->
        <section v-else-if="activeModule === 'sleep'" class="hws-card">
          <div class="hws-card-head">
            <h2 class="hws-card-title">睡眠趋势</h2>
          </div>
          <template v-if="!hasSleepData">
            <p class="hws-empty">睡眠时间尚未佩戴，暂无数据</p>
            <p v-if="sleepEmptyCopy" class="hws-hint hws-hint--sleep">{{ sleepEmptyCopy }}</p>
          </template>
          <template v-else>
            <div ref="sleepChart" class="hws-chart hws-chart--bar" />
            <ul class="hws-metric-list">
              <li v-for="item in sleepStats" :key="item.label" class="hws-metric-row">
                <span class="hws-metric-label">{{ item.label }}</span>
                <span class="hws-metric-value" :class="metricValueClass(item)">{{ item.value }}</span>
                <span class="hws-status" :class="metricStatusClass(item)">
                  {{ metricStatusIcon(item) }}
                </span>
              </li>
            </ul>
            <div v-if="sleepAiPoints.length" class="hws-ai-block">
              <div class="hws-sub-title">睡眠趋势 AI 分析</div>
              <ul class="hws-advice">
                <li v-for="(tip, i) in sleepAiPoints" :key="'sleep-ai-' + i">{{ tip }}</li>
              </ul>
            </div>
          </template>
        </section>
      </div>

      <p v-if="disclaimer" class="hws-disclaimer">{{ disclaimer }}</p>
      </template>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapState, mapActions } from 'pinia'
import { useGlobalStore } from '@/stores/global'
import { useHealthReportV2UnreadStore } from '@/stores/healthReportV2Unread'
import {
  getHealthReportWeeklyV2,
  getHealthReportWeeklyV2Detail,
  markHealthReportV2AsRead
} from '@/api/activity'
import {
  isReportOk,
  metricRow,
  formatMd,
  formatCnDate,
  zipRange,
  pad7,
  highlightSummary,
  splitAdvice,
  mapWarnTypes,
  buildWarnSeries,
  groupMeterRanges,
  mapTcm,
  resolveWeeklyReportDate,
  hasRealMetric,
  hasNumericPoints,
  scoreToneClass
} from '@/utils/healthReportV2'

/** 与本周要点高亮文字一致的健康状态色 */
const WEEKLY_STATUS_COLORS = {
  优秀: '#00bb82',
  良好: '#ff9800',
  一般: '#e53935'
}

/**
 * 健康周报展示页（V2 接口联调）
 */
export default {
  name: 'HealthWeeklyShowcase',
  data() {
    return {
      loading: true,
      loadError: '',
      disclaimer: '',
      dateRange: '',
      weekStart: '',
      weekDates: [],
      weekLabels: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
      /** 当前模块 Tab */
      activeModule: 'trend',
      /** 模块 Tab 左侧是否还有可滑内容 */
      tabsCanScrollLeft: false,
      /** 模块 Tab 右侧是否还有可滑内容 */
      tabsCanScrollRight: false,
      moduleTabs: [
        { key: 'trend', label: '健康趋势' },
        { key: 'warn', label: '健康预警' },
        { key: 'tcm', label: '中医五藏' },
        { key: 'hr', label: '心率趋势' },
        { key: 'bp', label: '血压趋势' },
        { key: 'sleep', label: '睡眠趋势' }
      ],
      trendUseScore: true,
      trendScores: [],
      trendStatuses: [],
      insights: [],
      warnSeries: [],
      warnTypes: [],
      warnEmptyCopy: '',
      tcmEvaluable: false,
      tcmOrgans: [],
      organTabs: [
        { key: 'heart', label: '心' },
        { key: 'liver', label: '肝' },
        { key: 'spleen', label: '脾' },
        { key: 'kidney', label: '肾' }
      ],
      activeOrgan: 'heart',
      tcmDailyScores: {
        heart: [null, null, null, null, null, null, null],
        liver: [null, null, null, null, null, null, null],
        spleen: [null, null, null, null, null, null, null],
        kidney: [null, null, null, null, null, null, null]
      },
      hrDateRange: '',
      hrRangeText: '暂无',
      hrPeakDay: '',
      hrRanges: [null, null, null, null, null, null, null],
      hrMatchedMin: null,
      hrMatchedMax: null,
      hrWeekStats: [],
      hrIdealRanges: [],
      hrAbnormalText: '',
      heartAiAdvice: '',
      heartAiAdviceList: [],
      hasWatchBp: false,
      watchBpEmptyCopy: '本周暂无手表血压数据',
      watchBpRanges: [null, null, null, null, null, null, null],
      watchBpStats: [],
      hasMeterBp: false,
      meterEmptyCopy: '',
      meterBpRanges: [null, null, null, null, null, null, null],
      bpIdealDiastolic: null,
      bpIdealSystolic: null,
      /** 手表血压异常阈值：收缩压 ≥ High */
      bpAbnormalSystolicHigh: null,
      /** 手表血压异常阈值：舒张压 ≤ Low */
      bpAbnormalDiastolicLow: null,
      bpRefRanges: [],
      bpAdviceList: [],
      hasSleepData: false,
      sleepEmptyCopy: '',
      sleepScores: [null, null, null, null, null, null, null],
      sleepYMax: 480,
      sleepStats: [],
      sleepAiPoints: [],
      chartMap: {}
    }
  },
  computed: {
    /**
     * @returns {Object|null}
     */
    currentTcmOrgan() {
      return this.tcmOrgans.find((d) => d.key === this.activeOrgan) || null
    },
    /**
     * @returns {string}
     */
    currentOrganLabel() {
      const org = this.organTabs.find((d) => d.key === this.activeOrgan)
      return (org && org.label) || ''
    },
    /**
     * @returns {string}
     */
    currentOrganStatus() {
      return (this.currentTcmOrgan && this.currentTcmOrgan.statusName) || ''
    },
    /**
     * @returns {string}
     */
    currentOrganAssessTitle() {
      const names = { heart: '心脏', liver: '肝脏', spleen: '脾脏', kidney: '肾脏' }
      return (names[this.activeOrgan] || this.currentOrganLabel || '') + '态势评估'
    },
    /**
     * 当前脏腑一周综合分（null 不补 0）
     * @returns {(number|null)[]}
     */
    currentOrganScores() {
      return (this.tcmDailyScores && this.tcmDailyScores[this.activeOrgan]) || []
    },
    /**
     * @returns {boolean}
     */
    hasCurrentOrganScores() {
      return this.currentOrganScores.some((v) => v != null)
    },
    /**
     * @returns {Array}
     */
    organDimensions() {
      return (this.currentTcmOrgan && this.currentTcmOrgan.dims) || []
    },
    /**
     * @returns {{ symptoms?: string, diseases?: string }}
     */
    currentHeartDim() {
      return this.organDimensions[0] || { symptoms: '暂无', diseases: '暂无' }
    },
    /**
     * 有数据才展示的模块 Tab
     * @returns {Array<{ key: string, label: string }>}
     */
    visibleModuleTabs() {
      return (this.moduleTabs || []).filter((tab) => this.isModuleTabVisible(tab.key))
    },
    /**
     * 是否有心率趋势数据
     * @returns {boolean}
     */
    hasHrData() {
      return (this.hrRanges || []).some((r) => r != null)
    },
    /**
     * 是否有健康趋势数据
     * @returns {boolean}
     */
    hasTrendData() {
      const hasScore = (this.trendScores || []).some((v) => v != null)
      return hasScore || (this.insights || []).length > 0
    },
    ...mapState(useGlobalStore, ['statusBarHeight'])
  },
  watch: {
    '$route.query.reportDate'() {
      this.fetchReport()
    },
    '$route.query.reportId'() {
      this.fetchReport()
    },
    '$route.query.tab'(val) {
      this.syncActiveModuleFromQuery(val)
      if (this.loading) return
      this.disposeCharts()
      this.$nextTick(() => {
        this.scrollActiveModuleTabIntoView()
        this.initChartsForModule(this.activeModule)
      })
    },
    activeOrgan() {
      if (this.activeModule !== 'tcm') return
      this.$nextTick(() => {
        if (this.activeModule !== 'tcm') return
        this.renderOrganScoreChart()
      })
    },
    visibleModuleTabs() {
      this.ensureActiveModuleVisible()
      this.$nextTick(() => {
        this.updateTabsScrollHint()
      })
    }
  },
  mounted() {
    document.title = '健康周报'
    this.syncActiveModuleFromQuery()
    this.fetchReport()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    ...mapActions(useHealthReportV2UnreadStore, {
      applyLocalWeeklyRead: 'applyLocalWeeklyRead',
      fetchReportUnread: 'fetchUnread'
    }),
    scoreToneClass,
    /**
     * 指标数值样式：异常红、暂无灰
     * @param {{ warn?: boolean, empty?: boolean }} item
     * @returns {Object}
     */
    metricValueClass(item) {
      return {
        danger: !!(item && item.warn),
        muted: !!(item && item.empty)
      }
    },
    /**
     * 指标状态圆标样式
     * @param {{ warn?: boolean, empty?: boolean }} item
     * @returns {string}
     */
    metricStatusClass(item) {
      if (item && item.empty) return 'empty'
      if (item && item.warn) return 'warn'
      return 'ok'
    },
    /**
     * 指标状态圆标图标
     * @param {{ warn?: boolean, empty?: boolean }} item
     * @returns {string}
     */
    metricStatusIcon(item) {
      if (item && item.empty) return '!'
      if (item && item.warn) return '!'
      return '✓'
    },
    /**
     * 打开报告后标记已读（详情不会自动已读，须显式调用）
     * @param {Object} data
     * @returns {Promise<void>}
     */
    async markReportReadIfNeeded(data) {
      const reportId = data && data.id != null ? String(data.id).trim() : ''
      if (!reportId || data.isRead === true) return
      try {
        const res = await markHealthReportV2AsRead(reportId)
        if (res && (res.code === '1000' || res.code === 1000)) {
          data.isRead = true
          this.applyLocalWeeklyRead()
          this.fetchReportUnread()
        }
      } catch (e) {
        console.error('标记周报已读失败:', e)
      }
    },
    /**
     * 拉取周报并渲染
     * @returns {Promise<void>}
     */
    async fetchReport() {
      this.loading = true
      this.loadError = ''
      this.disposeCharts()
      try {
        const reportId = this.$route.query.reportId
          ? String(this.$route.query.reportId).trim()
          : ''
        let res
        if (reportId) {
          res = await getHealthReportWeeklyV2Detail(reportId)
        } else {
          const reportDate = resolveWeeklyReportDate(this.$route)
          res = await getHealthReportWeeklyV2({ reportDate: reportDate })
        }
        if (!isReportOk(res)) {
          throw new Error((res && res.msg) || '加载健康周报失败')
        }
        this.applyWeekly(res.data)
        this.markReportReadIfNeeded(res.data)
        this.syncActiveModuleFromQuery()
        this.ensureActiveModuleVisible()
        this.loading = false
        await this.$nextTick()
        this.scrollActiveModuleTabIntoView()
        this.initAllCharts()
      } catch (e) {
        this.loadError = (e && e.message) || '加载健康周报失败'
        this.loading = false
      }
    },
    /**
     * 模块 Tab 是否有可展示数据
     * @param {string} key
     * @returns {boolean}
     */
    isModuleTabVisible(key) {
      if (key === 'trend') return !!this.hasTrendData
      if (key === 'warn') return (this.warnTypes || []).length > 0
      if (key === 'tcm') return !!this.tcmEvaluable
      if (key === 'hr') return !!this.hasHrData
      if (key === 'bp') return !!(this.hasWatchBp || this.hasMeterBp)
      if (key === 'sleep') return !!this.hasSleepData
      return true
    },
    /**
     * 当前 Tab 被隐藏时，落到第一个有数据的模块
     * @returns {void}
     */
    ensureActiveModuleVisible() {
      const tabs = this.visibleModuleTabs || []
      if (!tabs.length) return
      if (tabs.some((item) => item.key === this.activeModule)) return
      this.activeModule = tabs[0].key
    },
    /**
     * 根据路由 query.tab 同步模块 Tab
     * @param {string} [tab]
     * @returns {void}
     */
    syncActiveModuleFromQuery(tab) {
      const raw = tab != null ? tab : (this.$route.query && this.$route.query.tab)
      const key = String(raw || '').trim()
      if (!key) return
      if (!this.isModuleTabVisible(key)) return
      const matched = (this.moduleTabs || []).some((item) => item.key === key)
      if (matched) this.activeModule = key
    },
    /**
     * 更新模块 Tab 左右滑动提示状态
     * @returns {void}
     */
    updateTabsScrollHint() {
      const el = this.$refs.moduleTabs
      if (!el) {
        this.tabsCanScrollLeft = false
        this.tabsCanScrollRight = false
        return
      }
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll <= 2) {
        this.tabsCanScrollLeft = false
        this.tabsCanScrollRight = false
        return
      }
      this.tabsCanScrollLeft = el.scrollLeft > 2
      this.tabsCanScrollRight = el.scrollLeft < maxScroll - 2
    },
    /**
     * 将当前激活的模块 Tab 滚入可视区，并刷新滑动提示
     * @returns {void}
     */
    scrollActiveModuleTabIntoView() {
      const nav = this.$refs.moduleTabs
      if (!nav) return
      const active = nav.querySelector('.hws-module-tab.active')
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' })
      }
      this.updateTabsScrollHint()
    },
    /**
     * 返回上一页
     * @returns {void}
     */
    goBack() {
      this.$router.go(-1)
    },
    /**
     * 进入历史周报列表
     * @returns {void}
     */
    goHistoryList() {
      this.$router.push({ name: 'healthWeeklyShowcaseHistory' }).catch(() => {})
    },
    /**
     * 将 V2 周报 data 映射到页面
     * @param {Object} data
     * @returns {void}
     */
    applyWeekly(data) {
      const weekStart = data.weekStart || ''
      const weekEnd = data.weekEnd || data.reportDate || ''
      this.weekStart = weekStart
      this.dateRange = weekStart && weekEnd ? formatMd(weekStart) + ' ~ ' + formatMd(weekEnd) : ''
      this.hrDateRange = weekStart && weekEnd ? formatCnDate(weekStart) + ' - ' + formatCnDate(weekEnd) : ''
      this.disclaimer = data.disclaimer || ''
      this.heartAiAdvice = data.heartAiAdvice || ''

      const points = (data.trend && data.trend.points) || []
      if (points.length) {
        this.weekLabels = points.map((p) => p.weekday || '')
        this.weekDates = points.map((p) => p.date)
        // 趋势图按健康评分绘制；无数据保持 null，不填 0
        this.trendUseScore = true
        this.trendScores = points.map((p) => (
          p.healthScore != null ? Number(p.healthScore) : null
        ))
        this.trendStatuses = points.map((p) => this.resolveTrendStatus(p))
      } else {
        this.weekLabels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
        this.weekDates = []
        this.trendUseScore = true
        this.trendScores = [null, null, null, null, null, null, null]
        this.trendStatuses = ['', '', '', '', '', '', '']
      }
      this.insights = ((data.trend && data.trend.summaries) || []).map(highlightSummary)

      const warn = data.warn || {}
      this.warnTypes = mapWarnTypes(warn.typeCounts)
      this.warnSeries = buildWarnSeries(warn, weekStart)
      this.warnEmptyCopy = warn.emptyCopy || (this.warnTypes.length ? '' : '本周暂无健康预警')

      const tcm = data.tcm || {}
      this.tcmEvaluable = !!tcm.evaluable
      const mapped = mapTcm(tcm)
      this.organTabs = mapped.tabs
      this.tcmOrgans = mapped.organs
      this.activeOrgan = mapped.active
      this.tcmDailyScores = mapped.dailyScores

      const heart = data.heart || {}
      this.hrMatchedMin = heart.matchedHrMin != null ? Number(heart.matchedHrMin) : null
      this.hrMatchedMax = heart.matchedHrMax != null ? Number(heart.matchedHrMax) : null
      this.hrRanges = zipRange(heart.detailsLow, heart.detailsHi, 7)
      let minV = null
      let maxV = null
      let peakIdx = -1
      this.hrRanges.forEach((r, i) => {
        if (!r) return
        if (minV == null || r[0] < minV) minV = r[0]
        if (maxV == null || r[1] > maxV) {
          maxV = r[1]
          peakIdx = i
        }
      })
      this.hrRangeText = minV != null ? minV + '-' + maxV : '暂无'
      this.hrPeakDay = peakIdx >= 0 ? this.weekLabels[peakIdx] || '' : ''
      this.hrWeekStats = [
        metricRow(heart.avg, '平均心率'),
        metricRow(heart.max, '最高值'),
        metricRow(heart.min, '最低值'),
        metricRow(heart.resting, '静息心率')
      ]
      this.hrIdealRanges = this.buildHrIdealRanges(heart)
      this.hrAbnormalText = highlightSummary(heart.weeklyWarnCopy || '')
      this.heartAiAdvice = data.heartAiAdvice || ''
      this.heartAiAdviceList = data.heartAiAdvice ? splitAdvice(data.heartAiAdvice) : []

      const wbp = data.watchBloodPressure || {}
      this.watchBpEmptyCopy = wbp.emptyCopy || '本周暂无手表血压数据'
      this.hasWatchBp = !wbp.emptyCopy && (
        hasRealMetric(wbp.avgSystolic) ||
        hasRealMetric(wbp.avgDiastolic) ||
        hasNumericPoints(wbp.dailyAvgSystolic) ||
        hasNumericPoints(wbp.dailyAvgDiastolic)
      )
      // 手表血压图：仅用每日平均舒张/收缩压，无数据则显示空
      this.watchBpRanges = zipRange(wbp.dailyAvgDiastolic, wbp.dailyAvgSystolic, 7)
      this.bpAbnormalSystolicHigh = wbp.abnormalSystolicHigh != null
        ? Number(wbp.abnormalSystolicHigh)
        : null
      this.bpAbnormalDiastolicLow = wbp.abnormalDiastolicLow != null
        ? Number(wbp.abnormalDiastolicLow)
        : null
      this.watchBpStats = [
        metricRow(wbp.avgSystolic, '平均收缩压'),
        metricRow(wbp.avgDiastolic, '平均舒张压'),
        metricRow(wbp.maxSystolic, '最高收缩压'),
        metricRow(wbp.maxDiastolic, '最高舒张压')
      ]

      const mbp = data.monitorBloodPressure || {}
      this.hasMeterBp = !!mbp.measured
      this.meterEmptyCopy = mbp.emptyCopy || '本周未测血压。建议养成定期测量习惯，捕捉血压波动是预防疾病的关键。'
      this.meterBpRanges = groupMeterRanges(mbp.records, weekStart)
      this.bpRefRanges = []
      if (wbp.idealSystolic != null || mbp.idealSystolic != null) {
        const src = mbp.idealSystolic != null ? mbp : wbp
        this.bpIdealSystolic = src.idealSystolic != null ? Number(src.idealSystolic) : null
        this.bpIdealDiastolic = src.idealDiastolic != null ? Number(src.idealDiastolic) : null
        this.bpRefRanges = [
          { label: '理想收缩压', value: '< ' + src.idealSystolic + 'mmHg' },
          { label: '理想舒张压', value: '< ' + src.idealDiastolic + 'mmHg' }
        ]
      } else {
        this.bpIdealSystolic = null
        this.bpIdealDiastolic = null
      }
      this.bpAdviceList = data.bloodPressureAiAdvice
        ? splitAdvice(data.bloodPressureAiAdvice)
        : (Array.isArray(mbp.healthTips) ? mbp.healthTips.filter(Boolean) : [])

      const sleep = data.sleep || {}
      this.hasSleepData = !!sleep.hasData
      const sleepTip = '睡眠时间尚未佩戴，暂无数据'
      const sleepCopy = String(sleep.emptyCopy || '').trim()
      this.sleepEmptyCopy = sleepCopy && sleepCopy !== sleepTip ? sleepCopy : ''
      this.sleepScores = pad7(sleep.dailyTotalMinutes)
      const mins = this.sleepScores.filter((v) => v != null).map(Number)
      const maxMin = mins.length ? Math.max.apply(null, mins) : 0
      this.sleepYMax = Math.max(120, Math.ceil((maxMin * 1.2) / 60) * 60)
      this.sleepStats = (sleep.averages || []).map((m) => metricRow(m))
      if (!this.sleepStats.length && sleep.hasData) {
        this.sleepStats = [
          {
            label: '睡眠平均心率',
            value: sleep.avgHeart != null ? sleep.avgHeart + '次/分钟' : '暂无',
            warn: false,
            empty: sleep.avgHeart == null
          },
          {
            label: '睡眠低血氧异常次数',
            value: sleep.lowOxygenAbnormalTotal != null ? sleep.lowOxygenAbnormalTotal + '次' : '暂无',
            warn: sleep.lowOxygenAbnormalTotal != null && Number(sleep.lowOxygenAbnormalTotal) > 0,
            empty: sleep.lowOxygenAbnormalTotal == null
          },
          {
            label: '平均深睡时长',
            value: sleep.avgDeepSleepMinutes != null ? sleep.avgDeepSleepMinutes + '分钟' : '暂无',
            warn: false,
            empty: sleep.avgDeepSleepMinutes == null
          },
          {
            label: '平均夜间清醒次数',
            value: sleep.avgWakeCount != null ? String(sleep.avgWakeCount) + '次' : '暂无',
            warn: sleep.avgWakeCount != null && Number(sleep.avgWakeCount) > 3,
            empty: sleep.avgWakeCount == null
          }
        ]
      }
      this.sleepAiPoints = data.sleepAiAdvice ? splitAdvice(data.sleepAiAdvice) : []
    },
    /**
     * 销毁图表实例
     * @returns {void}
     */
    disposeCharts() {
      Object.keys(this.chartMap).forEach((key) => {
        const chart = this.chartMap[key]
        if (chart) {
          try {
            chart.dispose()
          } catch (e) {
            /* ignore disposed dom */
          }
        }
      })
      this.chartMap = {}
    },
    /**
     * 切换模块 Tab
     * @param {string} key
     * @returns {void}
     */
    switchModule(key) {
      if (!key || this.activeModule === key) return
      this.disposeCharts()
      this.activeModule = key
      this.$nextTick(() => {
        if (this.activeModule !== key) return
        this.scrollActiveModuleTabIntoView()
        this.initChartsForModule(key)
      })
    },
    /**
     * 按当前模块初始化图表（v-if 切换后需重建）
     * @param {string} [moduleKey]
     * @returns {void}
     */
    initChartsForModule(moduleKey) {
      const key = moduleKey || this.activeModule
      if (key === 'trend') this.initTrendChart()
      if (key === 'warn' && this.warnSeries.length) this.initWarnChart()
      if (key === 'tcm') this.renderOrganScoreChart()
      if (key === 'hr') this.initHrRangeChart()
      if (key === 'bp') {
        if (this.hasWatchBp) this.initBpRangeChart('watchBpChart', this.watchBpRanges, 200, 'watch')
        if (this.hasMeterBp) this.initBpRangeChart('meterBpChart', this.meterBpRanges, 200, 'meter')
      }
      if (key === 'sleep' && this.hasSleepData) {
        this.initScoreBar('sleepChart', this.sleepScores, '#00bb82', this.sleepYMax, true, false, true)
      }
    },
    /**
     * 初始化当前模块图表
     * @returns {void}
     */
    initAllCharts() {
      this.initChartsForModule(this.activeModule)
    },
    /**
     * 绑定/更新 chart 实例
     * @param {string} key
     * @param {HTMLElement} el
     * @returns {echarts.ECharts|null}
     */
    ensureChart(key, el) {
      if (!el) return null
      const existing = this.chartMap[key]
      if (existing) {
        if (existing.getDom && existing.getDom() === el) return existing
        try {
          existing.dispose()
        } catch (e) {
          /* ignore */
        }
        delete this.chartMap[key]
      }
      const mounted = echarts.getInstanceByDom(el)
      if (mounted) {
        try {
          mounted.dispose()
        } catch (e) {
          /* ignore */
        }
      }
      this.chartMap[key] = echarts.init(el)
      return this.chartMap[key]
    },
    /**
     * 按当前脏腑日序列绘制柱图；无分或字段缺失不画 0
     * @returns {void}
     */
    renderOrganScoreChart() {
      if (this.activeModule !== 'tcm') return
      if (this.chartMap.organScoreChart) {
        try {
          this.chartMap.organScoreChart.dispose()
        } catch (e) {
          /* ignore */
        }
        delete this.chartMap.organScoreChart
      }
      if (!this.tcmEvaluable || !this.hasCurrentOrganScores) return
      this.initScoreBar('organScoreChart', this.currentOrganScores, '#00bb82', 100, false, true)
    },
    /**
     * 构建心率参考区间展示项
     * @param {Object} heart
     * @returns {Array<{label: string, value: string}>}
     */
    buildHrIdealRanges(heart) {
      const ranges = []
      if (heart.matchedHrMin != null && heart.matchedHrMax != null) {
        ranges.push({
          label: '您的心率区间',
          value: heart.matchedHrMin + '～' + heart.matchedHrMax + ' 次/分钟'
        })
      }
      if (heart.idealMin != null && heart.idealMax != null) {
        ranges.push({
          label: '您的静息心率区间',
          value: heart.idealMin + '～' + heart.idealMax + ' 次/分钟'
        })
      }
      return ranges
    },
    /**
     * 判断当日心率区间是否在参考范围内
     * @param {(number[]|null)} range
     * @returns {boolean}
     */
    isHrRangeInMatched(range) {
      if (!range) return false
      if (this.hrMatchedMin == null || this.hrMatchedMax == null) return true
      const low = Number(range[0])
      const high = Number(range[1])
      return low >= this.hrMatchedMin && high <= this.hrMatchedMax
    },
    /**
     * 区间柱图渐变色（正常绿 / 异常红，沿用现有色值）
     * @param {boolean} isOk
     * @returns {echarts.graphic.LinearGradient}
     */
    buildRangeStatusGradient(isOk) {
      if (isOk) {
        return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#00bb82' },
          { offset: 1, color: '#4dcea0' }
        ])
      }
      return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: '#e53935' },
        { offset: 1, color: '#ffab9f' }
      ])
    },
    /**
     * 心率区间柱图（按是否在参考区间内着色）
     * @returns {void}
     */
    initHrRangeChart() {
      const chart = this.ensureChart('hrRangeChart', this.$refs.hrRangeChart)
      if (!chart) return
      const ranges = this.hrRanges || []
      const base = ranges.map((r) => (r ? r[0] : null))
      const height = ranges.map((r) => {
        if (!r) return null
        const inRange = this.isHrRangeInMatched(r)
        return {
          value: r[1] - r[0],
          itemStyle: {
            color: this.buildRangeStatusGradient(inRange),
            borderRadius: [4, 4, 4, 4]
          }
        }
      })
      chart.setOption({
        grid: { left: 36, right: 12, top: 20, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const idx = params && params[0] ? params[0].dataIndex : -1
            const r = ranges[idx]
            if (!r) return this.weekLabels[idx] + '：无数据'
            const inRange = this.isHrRangeInMatched(r)
            const statusText = this.hrMatchedMin != null && this.hrMatchedMax != null
              ? (inRange ? '<br/>在参考区间内' : '<br/>超出参考区间')
              : ''
            return this.weekLabels[idx] + '：' + r[0] + '-' + r[1] + statusText
          }
        },
        xAxis: {
          type: 'category',
          data: this.weekLabels,
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisTick: { show: false },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          max: 220,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: [
          {
            type: 'bar',
            stack: 'range',
            data: base,
            barWidth: 10,
            itemStyle: { color: 'transparent' },
            emphasis: { itemStyle: { color: 'transparent' } }
          },
          {
            type: 'bar',
            stack: 'range',
            data: height,
            barWidth: 10
          }
        ]
      }, true)
    },
    /**
     * 判断当日血压区间是否在理想参考范围内（血压计）
     * @param {(number[]|null)} range [舒张压, 收缩压]
     * @returns {boolean}
     */
    isBpRangeInIdeal(range) {
      if (!range) return false
      if (this.bpIdealDiastolic == null && this.bpIdealSystolic == null) return true
      const diastolic = Number(range[0])
      const systolic = Number(range[1])
      if (this.bpIdealDiastolic != null && diastolic >= this.bpIdealDiastolic) return false
      if (this.bpIdealSystolic != null && systolic >= this.bpIdealSystolic) return false
      return true
    },
    /**
     * 手表血压：按异常阈值判定是否正常（舒张压 ≤ Low 或 收缩压 ≥ High 为异常）
     * @param {(number[]|null)} range [舒张压, 收缩压]
     * @returns {boolean}
     */
    isWatchBpRangeOk(range) {
      if (!range) return false
      if (this.bpAbnormalSystolicHigh == null && this.bpAbnormalDiastolicLow == null) return true
      const diastolic = Number(range[0])
      const systolic = Number(range[1])
      if (this.bpAbnormalDiastolicLow != null && diastolic <= this.bpAbnormalDiastolicLow) return false
      if (this.bpAbnormalSystolicHigh != null && systolic >= this.bpAbnormalSystolicHigh) return false
      return true
    },
    /**
     * 血压区间柱图着色判定
     * @param {number[]|null} range
     * @param {'watch'|'meter'} [mode]
     * @returns {boolean}
     */
    isBpRangeOk(range, mode) {
      return mode === 'watch' ? this.isWatchBpRangeOk(range) : this.isBpRangeInIdeal(range)
    },
    /**
     * 血压区间柱图
     * @param {string} refKey
     * @param {(number[]|null)[]} ranges
     * @param {number} [yMax]
     * @param {'watch'|'meter'} [mode]
     * @returns {void}
     */
    initBpRangeChart(refKey, ranges, yMax, mode) {
      const chart = this.ensureChart(refKey, this.$refs[refKey])
      if (!chart) return
      const chartMode = mode || 'meter'
      const hasThreshold = chartMode === 'watch'
        ? (this.bpAbnormalSystolicHigh != null || this.bpAbnormalDiastolicLow != null)
        : (this.bpIdealDiastolic != null || this.bpIdealSystolic != null)
      const base = ranges.map((r) => (r ? r[0] : null))
      const height = ranges.map((r) => {
        if (!r) return null
        const inRange = this.isBpRangeOk(r, chartMode)
        return {
          value: r[1] - r[0],
          itemStyle: {
            color: this.buildRangeStatusGradient(inRange),
            borderRadius: [4, 4, 4, 4]
          }
        }
      })
      chart.setOption({
        grid: { left: 36, right: 12, top: 20, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const idx = params && params[0] ? params[0].dataIndex : -1
            const r = ranges[idx]
            if (!r) return this.weekLabels[idx] + '：无数据'
            const inRange = this.isBpRangeOk(r, chartMode)
            const statusText = hasThreshold
              ? (inRange ? '<br/>在参考区间内' : '<br/>超出参考区间')
              : ''
            return this.weekLabels[idx] + '：' + r[0] + '-' + r[1] + ' mmHg' + statusText
          }
        },
        xAxis: {
          type: 'category',
          data: this.weekLabels,
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisTick: { show: false },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          max: yMax || 200,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: [
          {
            type: 'bar',
            stack: 'range',
            data: base,
            barWidth: 10,
            itemStyle: { color: 'transparent' },
            emphasis: { itemStyle: { color: 'transparent' } }
          },
          {
            type: 'bar',
            stack: 'range',
            data: height,
            barWidth: 10
          }
        ]
      }, true)
    },
    /**
     * 解析趋势点健康状态（用于着色，与本周要点文案一致）
     * @param {{ overallStatus?: string, healthScore?: number }} point
     * @returns {string}
     */
    resolveTrendStatus(point) {
      if (point && point.overallStatus) return point.overallStatus
      if (point && point.healthScore != null) {
        const score = Number(point.healthScore)
        if (score >= 80) return '优秀'
        if (score >= 60) return '良好'
        if (score > 0) return '一般'
      }
      return ''
    },
    /**
     * 趋势状态对应颜色（无状态为灰，仅用于点）
     * @param {string} status
     * @returns {string}
     */
    trendStatusColor(status) {
      return WEEKLY_STATUS_COLORS[status] || '#cfd8dc'
    },
    /**
     * 趋势点状态色（无数据为灰）
     * @param {number} index
     * @param {(number|null)[]} scores
     * @param {string[]} statuses
     * @returns {string}
     */
    resolveTrendPointColor(index, scores, statuses) {
      const score = scores[index]
      if (score == null) return '#cfd8dc'
      const status = statuses[index] || ''
      return this.trendStatusColor(status)
    },
    /**
     * 构建相邻有效点线段
     * @param {(number|null)[]} scores
     * @param {string[]} statuses
     * @returns {Array<{ from: number, to: number, valueFrom: number, valueTo: number, colorStart: string, colorEnd: string }>}
     */
    buildTrendSegmentData(scores, statuses) {
      const list = scores || []
      const segments = []
      for (let i = 0; i < list.length - 1; i += 1) {
        if (list[i] == null || list[i + 1] == null) continue
        segments.push({
          from: i,
          to: i + 1,
          valueFrom: list[i],
          valueTo: list[i + 1],
          colorStart: this.resolveTrendPointColor(i, list, statuses),
          colorEnd: this.resolveTrendPointColor(i + 1, list, statuses)
        })
      }
      return segments
    },
    /**
     * 两色 hex 插值（正弦缓动，中间过渡更柔和）
     * @param {string} fromHex
     * @param {string} toHex
     * @param {number} t 0~1
     * @returns {string}
     */
    interpolateHexColor(fromHex, toHex, t) {
      const parse = (hex) => {
        const raw = String(hex || '').replace('#', '')
        if (raw.length !== 6) return [0, 187, 130]
        return [
          parseInt(raw.slice(0, 2), 16),
          parseInt(raw.slice(2, 4), 16),
          parseInt(raw.slice(4, 6), 16)
        ]
      }
      const ratio = Math.max(0, Math.min(1, Number(t) || 0))
      // 正弦缓动：两端更稳、中间更柔
      const eased = 0.5 - 0.5 * Math.cos(Math.PI * ratio)
      const a = parse(fromHex)
      const b = parse(toHex)
      const mix = (i) => Math.round(a[i] + (b[i] - a[i]) * eased)
      return 'rgb(' + mix(0) + ',' + mix(1) + ',' + mix(2) + ')'
    },
    /**
     * Catmull-Rom 采样，让过渡线更顺滑
     * @param {number} p0
     * @param {number} p1
     * @param {number} p2
     * @param {number} p3
     * @param {number} t
     * @returns {number}
     */
    sampleCatmullRom(p0, p1, p2, p3, t) {
      const t2 = t * t
      const t3 = t2 * t
      return 0.5 * (
        (2 * p1) +
        (-p0 + p2) * t +
        (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
        (-p0 + 3 * p1 - 3 * p2 + p3) * t3
      )
    },
    /**
     * 构建平滑面积折线采样点
     * @param {(number|null)[]} scores
     * @returns {Array<[number, number]>}
     */
    buildTrendSmoothAreaData(scores) {
      const list = scores || []
      const segs = this.buildTrendSegmentData(list, this.trendStatuses || [])
      const areaData = []
      const steps = 32
      segs.forEach((seg, segIdx) => {
        const y0 = list[seg.from - 1] != null ? Number(list[seg.from - 1]) : seg.valueFrom
        const y1 = seg.valueFrom
        const y2 = seg.valueTo
        const y3 = list[seg.to + 1] != null ? Number(list[seg.to + 1]) : seg.valueTo
        const start = segIdx === 0 ? 0 : 1
        for (let s = start; s <= steps; s += 1) {
          const t = s / steps
          areaData.push([seg.from + t, this.sampleCatmullRom(y0, y1, y2, y3, t)])
        }
      })
      return areaData
    },
    /**
     * 自定义系列：整段平滑 polyline + 全局坐标渐变，避免分段发虚
     * @param {(number|null)[]} scores
     * @param {string[]} statuses
     * @returns {object}
     */
    buildTrendGradientCustomSeries(scores, statuses) {
      const list = scores || []
      const segments = this.buildTrendSegmentData(list, statuses)
      const self = this
      return {
        type: 'custom',
        name: 'trendGradientLine',
        clip: true,
        coordinateSystem: 'cartesian2d',
        data: segments.map((seg) => [seg.from, seg.valueFrom]),
        renderItem(params, api) {
          const seg = segments[params.dataIndex]
          if (!seg) return null
          const y0 = list[seg.from - 1] != null ? Number(list[seg.from - 1]) : seg.valueFrom
          const y1 = seg.valueFrom
          const y2 = seg.valueTo
          const y3 = list[seg.to + 1] != null ? Number(list[seg.to + 1]) : seg.valueTo
          const steps = 40
          const points = []
          for (let s = 0; s <= steps; s += 1) {
            const t = s / steps
            const v = self.sampleCatmullRom(y0, y1, y2, y3, t)
            const p = api.coord([seg.from + t, v])
            if (p) points.push(p)
          }
          if (points.length < 2) return null
          const pStart = points[0]
          const pEnd = points[points.length - 1]
          const colorStops = []
          const stopCount = 10
          for (let i = 0; i <= stopCount; i += 1) {
            const t = i / stopCount
            colorStops.push({
              offset: t,
              color: self.interpolateHexColor(seg.colorStart, seg.colorEnd, t)
            })
          }
          return {
            type: 'polyline',
            silent: true,
            shape: {
              points: points,
              smooth: 0
            },
            style: {
              stroke: new echarts.graphic.LinearGradient(
                pStart[0], pStart[1],
                pEnd[0], pEnd[1],
                colorStops,
                true
              ),
              lineWidth: 3,
              lineCap: 'round',
              lineJoin: 'round',
              fill: 'none'
            }
          }
        },
        z: 2,
        silent: true,
        tooltip: { show: false },
        animation: false
      }
    },
    /**
     * @returns {void}
     */
    initTrendChart() {
      const chart = this.ensureChart('trendChart', this.$refs.trendChart)
      if (!chart) return
      const scores = (this.trendScores || []).map((v) => (v == null ? null : Number(v)))
      const statuses = this.trendStatuses || []
      const yMax = 100
      let firstColor = '#00bb82'
      for (let i = 0; i < scores.length; i += 1) {
        if (scores[i] != null) {
          firstColor = this.resolveTrendPointColor(i, scores, statuses)
          break
        }
      }
      const areaData = this.buildTrendSmoothAreaData(scores)
      const gradientSeries = this.buildTrendGradientCustomSeries(scores, statuses)
      const pointData = scores.map((score, index) => {
        if (score == null) return null
        return {
          value: [index, score],
          weekIndex: index,
          itemStyle: {
            color: this.resolveTrendPointColor(index, scores, statuses),
            borderColor: '#fff',
            borderWidth: 2
          }
        }
      }).filter(function (item) { return item != null })

      chart.setOption({
        grid: { left: 40, right: 16, top: 24, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          formatter: (params) => {
            const list = Array.isArray(params) ? params : [params]
            const p = list.find((item) => item.seriesName === 'trendPoints')
            if (!p || !p.data) return ''
            const weekIndex = p.data.weekIndex != null ? p.data.weekIndex : p.dataIndex
            const rawVal = p.value
            const scoreVal = Array.isArray(rawVal) ? rawVal[1] : rawVal
            if (scoreVal == null || scoreVal === '') {
              return (this.weekLabels[weekIndex] || '') + '<br/>暂无数据'
            }
            const status = statuses[weekIndex] || '暂无'
            return (this.weekLabels[weekIndex] || '') +
              '<br/>评分：' + scoreVal + '分<br/>状态：' + status
          }
        },
        xAxis: {
          type: 'value',
          min: 0,
          max: Math.max((this.weekLabels || []).length - 1, 1),
          interval: 1,
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: {
            color: '#8a9691',
            fontSize: 12,
            formatter: (v) => {
              const idx = Math.round(Number(v))
              if (Math.abs(Number(v) - idx) > 0.01) return ''
              return this.weekLabels[idx] || ''
            }
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: yMax,
          interval: yMax / 5,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: {
            color: '#8a9691',
            fontSize: 12,
            formatter: (v) => (v === 0 ? '0' : String(v))
          }
        },
        series: [
          {
            type: 'line',
            name: 'trendArea',
            data: areaData,
            smooth: false,
            showSymbol: false,
            symbol: 'none',
            lineStyle: { width: 0, opacity: 0, color: 'transparent' },
            emphasis: { disabled: true },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: this.hexToRgba(firstColor, 0.2) },
                { offset: 1, color: this.hexToRgba(firstColor, 0.02) }
              ])
            },
            z: 1,
            silent: true,
            tooltip: { show: false },
            animation: false
          },
          gradientSeries,
          {
            type: 'scatter',
            name: 'trendPoints',
            data: pointData,
            symbolSize: 10,
            z: 5,
            animation: false
          }
        ]
      }, true)
    },
    /**
     * hex 转 rgba
     * @param {string} hex
     * @param {number} alpha
     * @returns {string}
     */
    hexToRgba(hex, alpha) {
      const raw = String(hex || '').replace('#', '')
      if (raw.length !== 6) return 'rgba(0, 187, 130, ' + alpha + ')'
      const r = parseInt(raw.slice(0, 2), 16)
      const g = parseInt(raw.slice(2, 4), 16)
      const b = parseInt(raw.slice(4, 6), 16)
      return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')'
    },
    initWarnChart() {
      const chart = this.ensureChart('warnChart', this.$refs.warnChart)
      if (!chart) return
      const seriesMeta = this.warnSeries
      const self = this
      chart.off('click')
      chart.on('click', (p) => {
        const date = self.weekDates[p.dataIndex]
        if (!date) return
        self.$router.push({
          name: 'healthDailyShowcase',
          query: { reportDate: date, tab: 'warn' }
        })
      })
      chart.setOption({
        grid: { left: 28, right: 12, top: 28, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            if (!params || !params.length) return ''
            const lines = [params[0].axisValue]
            let total = 0
            params.forEach((p) => {
              const v = Number(p.value) || 0
              total += v
              lines.push(p.marker + p.seriesName + '：' + v + '次')
            })
            lines.push('合计：' + total + '次')
            return lines.join('<br/>')
          }
        },
        xAxis: {
          type: 'category',
          data: this.weekLabels,
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisTick: { show: false },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: seriesMeta.map((item, index) => ({
          name: item.name,
          type: 'bar',
          stack: 'warn',
          barWidth: 16,
          data: item.data,
          itemStyle: {
            color: item.color,
            borderRadius:
              index === seriesMeta.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
          },
          label: {
            show: index === seriesMeta.length - 1,
            position: 'top',
            color: '#667570',
            fontSize: 12,
            formatter: (p) => {
              const i = p.dataIndex
              const total = seriesMeta.reduce((sum, s) => sum + (Number(s.data[i]) || 0), 0)
              return total > 0 ? total : ''
            }
          }
        }))
      }, true)
    },
    /**
     * 按分数生成柱状图渐变色（与项目健康状态三色一致）
     * @param {number} score
     * @returns {echarts.graphic.LinearGradient}
     */
    buildScoreBarGradient(score) {
      const value = Number(score) || 0
      let darkColor = '#00bb82'
      let lightColor = '#1ad08f'
      if (value >= 80) {
        darkColor = '#00bb82'
        lightColor = '#1ad08f'
      } else if (value >= 60) {
        darkColor = '#ff9800'
        lightColor = '#ffc266'
      } else if (value > 0) {
        darkColor = '#ff8a80'
        lightColor = '#ffab9f'
      } else {
        darkColor = '#cfd8dc'
        lightColor = '#e0e6e3'
      }
      return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: darkColor },
        { offset: 1, color: lightColor }
      ])
    },
    /**
     * 评分 / 分钟柱状图
     * @param {string} refKey
     * @param {(number|null)[]} scores
     * @param {string} color
     * @param {number} [yMax]
     * @param {boolean} [asMinutes]
     * @param {boolean} [gradientByScore]
     * @param {boolean} [greenGradient]
     * @returns {void}
     */
    initScoreBar(refKey, scores, color, yMax, asMinutes, gradientByScore, greenGradient) {
      const chart = this.ensureChart(refKey, this.$refs[refKey])
      if (!chart) return
      const useItemGradient = gradientByScore || greenGradient
      chart.setOption({
        grid: { left: 28, right: 12, top: 28, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const p = params && params[0]
            if (!p) return ''
            if (p.value == null) return p.name + '：无数据'
            if (asMinutes) {
              const h = Math.floor(p.value / 60)
              const m = Math.round(p.value % 60)
              return p.name + '：' + (h ? h + '小时' : '') + (m ? m + '分钟' : (h ? '' : '0分钟'))
            }
            return p.name + '：' + p.value
          }
        },
        xAxis: {
          type: 'category',
          data: this.weekLabels,
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisTick: { show: false },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          max: yMax || 100,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: [
          {
            type: 'bar',
            data: scores.map((v) => {
              if (v == null) return null
              if (gradientByScore) {
                return {
                  value: v,
                  itemStyle: {
                    color: this.buildScoreBarGradient(v),
                    borderRadius: [6, 6, 0, 0]
                  }
                }
              }
              if (greenGradient) {
                return {
                  value: v,
                  itemStyle: {
                    color: this.buildRangeStatusGradient(true),
                    borderRadius: [6, 6, 0, 0]
                  }
                }
              }
              return v
            }),
            barWidth: 14,
            itemStyle: useItemGradient
              ? {}
              : { color: color, borderRadius: [6, 6, 0, 0] },
            label: {
              show: true,
              position: 'top',
              color: '#8a9691',
              fontSize: 12,
              formatter: (p) => {
                if (p.value == null) return ''
                if (asMinutes) return (p.value / 60).toFixed(1)
                return p.value
              }
            }
          }
        ]
      }, true)
    },
    /**
     * 区间柱图（min-max）
     * @param {string} refKey
     * @param {(number[]|null)[]} ranges
     * @param {string} color
     * @param {number} [yMax]
     * @returns {void}
     */
    initRangeChart(refKey, ranges, color, yMax) {
      const chart = this.ensureChart(refKey, this.$refs[refKey])
      if (!chart) return
      const base = ranges.map((r) => (r ? r[0] : null))
      const height = ranges.map((r) => (r ? r[1] - r[0] : null))
      chart.setOption({
        grid: { left: 36, right: 12, top: 20, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const idx = params && params[0] ? params[0].dataIndex : -1
            const r = ranges[idx]
            if (!r) return this.weekLabels[idx] + '：无数据'
            return this.weekLabels[idx] + '：' + r[0] + '-' + r[1]
          }
        },
        xAxis: {
          type: 'category',
          data: this.weekLabels,
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisTick: { show: false },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        yAxis: {
          type: 'value',
          max: yMax || 150,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: [
          {
            type: 'bar',
            stack: 'range',
            data: base,
            barWidth: 10,
            itemStyle: { color: 'transparent' },
            emphasis: { itemStyle: { color: 'transparent' } }
          },
          {
            type: 'bar',
            stack: 'range',
            data: height,
            barWidth: 10,
            itemStyle: { color: color, borderRadius: [4, 4, 4, 4] }
          }
        ]
      }, true)
    },
    handleResize() {
      this.updateTabsScrollHint()
      Object.keys(this.chartMap).forEach((key) => {
        const chart = this.chartMap[key]
        if (!chart || !chart.getDom) return
        const dom = chart.getDom()
        if (dom && dom.isConnected) {
          chart.resize()
        }
      })
    },
    /**
     * 预警分类点击：跳转本周第一天日报（类型无单日）
     * @param {{ name: string, count: number }} item
     * @returns {void}
     */
    onWarnClick(item) {
      const date = this.weekDates[0] || this.weekStart
      if (date) {
        this.$router.push({
          name: 'healthDailyShowcase',
          query: { reportDate: date, tab: 'warn' }
        })
        return
      }
      this.toast(item.name + '：' + item.count + '次')
    },
    /**
     * @param {string} msg
     * @returns {void}
     */
    toast(msg) {
      if (this.$toast) this.$toast(msg)
      else if (this.$message) this.$message.info(msg)
    }
  }
}
</script>

<style scoped>
.hws-page {
  position: relative;
  min-height: 100vh;
  background: #f5f7f8;
  overflow-x: hidden;
}
.hws-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.hws-bg-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #a0edd2 0%, #eefaf5 22%, #f5f7f8 48%, #f5f7f8 100%);
}
.hws-main {
  position: relative;
  z-index: 1;
  max-width: 480px;
  margin: 0 auto;
  padding: 0 16px 40px;
  box-sizing: border-box;
}
.hws-nav {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 44px;
  margin-bottom: 8px;
}
.hws-nav-back {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  background: transparent;
  color: #12231c;
  font-size: 22px;
}
.hws-nav-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  max-width: calc(100% - 160px);
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #000;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}
.hws-history-btn {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.78);
  color: #0f7a56;
  font-size: 12px;
  font-weight: 600;
  line-height: 28px;
}
.hws-history-btn:active {
  opacity: 0.85;
}
.hws-content-date {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #5f726a;
}
.hws-loading {
  text-align: center;
  padding: 80px 16px;
  color: #5f726a;
  font-size: 15px;
}
.hws-retry {
  margin-top: 16px;
  width: 140px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #00bb82;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}
.hws-disclaimer {
  margin: 16px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9aa3a0;
}
.hws-module-tabs-wrap {
  position: relative;
  margin: 0 -4px 14px;
}
.hws-module-tabs-wrap::before,
.hws-module-tabs-wrap::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 6px;
  width: 48px;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.hws-module-tabs-wrap::before {
  left: 0;
  background: linear-gradient(to right, #f5f7f8 35%, rgba(245, 247, 248, 0));
}
.hws-module-tabs-wrap::after {
  right: 0;
  background: linear-gradient(to left, #f5f7f8 35%, rgba(245, 247, 248, 0));
}
.hws-module-tabs-wrap.is-scroll-left::before,
.hws-module-tabs-wrap.is-scroll-right::after {
  opacity: 1;
}
.hws-tabs-hint {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-top: -15px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 187, 130, 0.18);
  color: #00bb82;
  box-shadow: 0 2px 10px rgba(18, 35, 28, 0.08);
  pointer-events: none;
  backdrop-filter: blur(4px);
}
.hws-tabs-hint--left {
  left: 2px;
  animation: hws-tabs-hint-pulse 1.6s ease-in-out infinite;
}
.hws-tabs-hint--right {
  right: 2px;
  animation: hws-tabs-hint-pulse 1.6s ease-in-out infinite;
}
@keyframes hws-tabs-hint-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.72; transform: scale(0.96); }
}
.hws-module-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 2px 4px 8px;
  scrollbar-width: none;
}
.hws-module-tabs::-webkit-scrollbar {
  display: none;
}
.hws-module-tab {
  flex: 0 0 auto;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  color: #5f726a;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 32px;
}
.hws-module-tab.active {
  background: linear-gradient(160deg, #2fd5a0 0%, #00bb82 55%, #00a572 100%);
  color: #fff;
}
.hws-module-body {
  min-height: 200px;
}
.hws-lead {
  margin: 8px 0 0;
  font-size: 14px;
  color: #667570;
}
.hws-status-board {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.hws-status-item {
  background: #fff;
  border-radius: 12px;
  padding: 12px 10px;
  text-align: center;
  box-shadow: 0 6px 16px rgba(18, 35, 28, 0.04);
  border-top: 3px solid var(--accent);
}
.hws-status-num {
  font-size: 25px;
  font-weight: 700;
  color: #12231c;
  line-height: 1.1;
}
.hws-status-label {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent);
}
.hws-status-unit {
  font-size: 12px;
  color: #8a9691;
}
.hws-card {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 6px 18px rgba(18, 35, 28, 0.04);
}
.hws-card-note {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9aa5a0;
  text-align: center;
}
.hws-card-head { margin-bottom: 8px; }
.hws-card-head--warn {
  margin-bottom: 6px;
}
.hws-card-head--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.hws-card-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #12231c;
}
.hws-sub-title {
  margin: 12px 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1a2a24;
}
.hws-muted {
  font-size: 13px;
  color: #8a9691;
}
.hws-hint {
  margin: 0 0 8px;
  font-size: 13px;
  color: #8a9691;
}
.hws-hint--sleep {
  margin: 0;
  text-align: center;
  line-height: 1.7;
  font-size: 14px;
  color: #667570;
}
.hws-chart { width: 100%; }
.hws-chart--trend { height: 200px; }
.hws-chart--warn { height: 200px; }
.hws-chart--bar { height: 190px; }
.hws-chart--range { height: 200px; }

.hws-insights {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hws-insight {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid #eef2f0;
}
.hws-insight:first-child {
  border-top: none;
  padding-top: 0;
}
.hws-insight-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #00bb82;
  margin-top: 8px;
  flex-shrink: 0;
}
.hws-insight-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: #4a5b54;
}
.hws-insight-text >>> .c-ok { color: #00bb82; font-weight: 600; }
.hws-insight-text >>> .c-good { color: #ff9800; font-weight: 600; }
.hws-insight-text >>> .c-fair { color: #ff8a80; font-weight: 600; }
.hws-insight-text >>> .c-danger { color: #e53935; font-weight: 700; }
.hws-insight-text >>> .c-link { color: #1e88e5; font-weight: 600; }
.hws-ai-text >>> .c-danger { color: #e53935; font-weight: 700; }

.hws-warn-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}
.hws-warn-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 4px;
  border-top: 1px solid #eef2f0;
  cursor: pointer;
}
.hws-warn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.hws-warn-name {
  flex: 1;
  font-size: 15px;
  color: #1a2a24;
}
.hws-warn-count {
  font-size: 15px;
  font-weight: 700;
  color: #e53935;
}
.hws-warn-arrow {
  color: #b0bbb6;
  font-size: 15px;
}

.hws-organ-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}
.hws-organ-tab {
  border: 1px solid #e3ebe7;
  background: #fff;
  color: #334640;
  border-radius: 999px;
  height: 34px;
  font-size: 14px;
  cursor: pointer;
}
.hws-organ-tab.active {
  background: linear-gradient(180deg, #1ad08f 0%, #00bb82 100%);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}
.hws-dim-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin: 8px 0 10px;
}
.hws-dim-tab {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(138, 150, 145, 0.08);
  border: 1px solid rgba(138, 150, 145, 0.14);
  box-sizing: border-box;
}
.hws-dim-tab.is-excellent {
  background: linear-gradient(160deg, #2fd5a0 0%, #00bb82 55%, #00a572 100%);
  border-color: transparent;
}
.hws-dim-tab.is-good {
  background: linear-gradient(160deg, #ffb347 0%, #ff9800 55%, #f08800 100%);
  border-color: transparent;
}
.hws-dim-tab.is-fair {
  background: linear-gradient(160deg, #ffb0a8 0%, #ff8a80 55%, #f07066 100%);
  border-color: transparent;
}
.hws-dim-tab-label {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: #6b7a74;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hws-dim-tab-score {
  flex-shrink: 0;
  display: inline-flex;
  align-items: baseline;
  font-size: 17px;
  font-weight: 700;
  color: #8a9691;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.hws-dim-tab.is-excellent .hws-dim-tab-label,
.hws-dim-tab.is-excellent .hws-dim-tab-score,
.hws-dim-tab.is-excellent .hws-dim-tab-unit,
.hws-dim-tab.is-good .hws-dim-tab-label,
.hws-dim-tab.is-good .hws-dim-tab-score,
.hws-dim-tab.is-good .hws-dim-tab-unit,
.hws-dim-tab.is-fair .hws-dim-tab-label,
.hws-dim-tab.is-fair .hws-dim-tab-score,
.hws-dim-tab.is-fair .hws-dim-tab-unit {
  color: #fff;
}
.hws-dim-tab-unit {
  margin-left: 1px;
  font-size: 11px;
  font-weight: 600;
  color: #8a9691;
}
.hws-dim-info {
  background: #f7faf9;
  border-radius: 10px;
  padding: 10px 12px;
}
.hws-dim-row {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
}
.hws-dim-label {
  color: #00a865;
  font-weight: 600;
  flex-shrink: 0;
}
.hws-dim-value { color: #4a5b54; }
.hws-empty {
  padding: 24px 0;
  text-align: center;
  color: #8a9691;
  font-size: 14px;
}

.hws-hero-metric { margin: 4px 0 8px; }
.hws-hero-value {
  font-size: 29px;
  font-weight: 700;
  color: #12231c;
}
.hws-hero-unit {
  margin-top: 4px;
  font-size: 13px;
  color: #8a9691;
}

.hws-metric-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hws-metric-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 0;
  border-top: 1px solid #eef2f0;
  font-size: 15px;
}
.hws-metric-row:first-child { border-top: none; }
.hws-metric-label {
  flex: 1;
  color: #4a5b54;
}
.hws-metric-value {
  color: #12231c;
  font-weight: 600;
}
.hws-metric-value.danger { color: #e53935; }
.hws-metric-value.muted { color: #9aa5a0; }
.hws-status {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}
.hws-status.ok { background: #00bb82; }
.hws-status.warn { background: #ff9800; }
.hws-status.empty { background: #b8c0bc; }

.hws-range-pills {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}
.hws-range-pill {
  background: rgba(0, 187, 130, 0.1);
  border-radius: 10px;
  padding: 10px 12px;
}
.hws-range-pill {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  color: #3d5a50;
}
.hws-range-pill strong { color: #0f7a56; }

.hws-ai-block { margin-top: 12px; }
.hws-advice {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hws-advice li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #12231c;
}
.hws-advice li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #12231c;
  opacity: 0.45;
}
.hws-advice li:last-child {
  margin: 12px 0 0;
  padding: 10px 12px 10px 28px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 187, 130, 0.08) 0%, rgba(0, 187, 130, 0.03) 100%);
  border: 1px solid rgba(0, 187, 130, 0.14);
  color: #0f7a56;
  font-weight: 500;
}
.hws-advice li:last-child::before {
  left: 12px;
  top: 16px;
  width: 6px;
  height: 6px;
  background: #00bb82;
  opacity: 1;
  box-shadow: 0 0 0 3px rgba(0, 187, 130, 0.16);
}
.hws-advice li:only-child {
  margin-top: 0;
}
.hws-advice-num {
  list-style: none;
  margin: 0;
  padding: 0;
  color: #12231c;
  font-size: 14px;
  line-height: 1.7;
}
.hws-advice-num li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 10px;
}
.hws-advice-num li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #12231c;
  opacity: 0.45;
}
.hws-advice-num li::marker {
  content: none;
}
.hws-advice-num li:last-child {
  margin: 12px 0 0;
  padding: 10px 12px 10px 28px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 187, 130, 0.08) 0%, rgba(0, 187, 130, 0.03) 100%);
  border: 1px solid rgba(0, 187, 130, 0.14);
  color: #0f7a56;
  font-weight: 500;
}
.hws-advice-num li:last-child::before {
  left: 12px;
  top: 16px;
  width: 6px;
  height: 6px;
  background: #00bb82;
  opacity: 1;
  box-shadow: 0 0 0 3px rgba(0, 187, 130, 0.16);
}
.hws-advice-num li:only-child {
  margin-top: 0;
}
.hws-ai-text {
  position: relative;
  margin: 0 0 10px;
  padding-left: 14px;
  font-size: 14px;
  line-height: 1.7;
  color: #12231c;
}
.hws-ai-text:last-child {
  margin: 12px 0 0;
  padding: 10px 12px 10px 28px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 187, 130, 0.08) 0%, rgba(0, 187, 130, 0.03) 100%);
  border: 1px solid rgba(0, 187, 130, 0.14);
  color: #0f7a56;
  font-weight: 500;
}
.hws-ai-text:only-child {
  margin-top: 0;
}
.hws-ai-text::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #12231c;
  opacity: 0.45;
}
.hws-ai-text:last-child::before {
  left: 12px;
  top: 16px;
  width: 6px;
  height: 6px;
  background: #00bb82;
  opacity: 1;
  box-shadow: 0 0 0 3px rgba(0, 187, 130, 0.16);
}
.hws-ai-link {
  display: block;
  margin-left: auto;
  margin-top: 8px;
  border: none;
  background: transparent;
  color: #ff9800;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.hws-card--bp-advice {
  background: linear-gradient(180deg, #ffffff 0%, #f9fcfb 100%);
}

.hws-bp-ref-section + .hws-bp-advice-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.hws-bp-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 700;
  color: #1a2a24;
}

.hws-bp-section-title--left {
  justify-content: flex-start;
}

.hws-bp-section-title-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.hws-bp-section-tag {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: #8a9691;
}

.hws-bp-section-bar {
  flex-shrink: 0;
  width: 3px;
  height: 14px;
  background: #00c275;
  border-radius: 2px;
}

.hws-bp-ref-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.hws-bp-ref-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 12px;
  background: linear-gradient(135deg, #f3fbf8 0%, #eefaf5 100%);
  border: 1px solid rgba(0, 194, 117, 0.14);
  border-radius: 12px;
}

.hws-bp-ref-item.is-diastolic {
  background: linear-gradient(135deg, #f5f9ff 0%, #eef5ff 100%);
  border-color: rgba(47, 124, 246, 0.14);
}

.hws-bp-ref-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #00a865;
  background: rgba(0, 187, 130, 0.14);
}

.hws-bp-ref-item.is-diastolic .hws-bp-ref-icon {
  color: #2f7cf6;
  background: rgba(47, 124, 246, 0.12);
}

.hws-bp-ref-content {
  flex: 1;
  min-width: 0;
}

.hws-bp-ref-label {
  font-size: 13px;
  color: #6b7a74;
  line-height: 1.4;
}

.hws-bp-ref-value {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #0f7a56;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hws-bp-ref-item.is-diastolic .hws-bp-ref-value {
  color: #2f6fd6;
}
</style>



// WEBPACK FOOTER //
// src/views/healthWeeklyShowcase/index.vue